import React, { useState } from 'react';
import {
  Grid,
  Typography,
  InputAdornment,
  Box,
  Divider,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useField } from 'formik';
import { InfoModal } from 'components/generics/InfoModal';
// import InputField from 'components/forms/InputField';
// import SwitchField from 'components/formFields/SwitchField';
import planesFormModel from '../../formik/planesFormModel';
// import { HelpModal } from 'components/forms/HelpModal';
import InputField from 'components/formFields/InputField';
import InputNumericMoney from 'components/formFields/InputNumericMoney';
import AutocompleteField from 'components/formFields/autocompleteField';
import { currencies } from 'commons/constants/currencies';
import InputUSD from 'components/formFields/InputUSD';

const CustomSwitch = ({
  fieldConfig,
  title,
  description,
  onOpenInfo,
}: {
  fieldConfig: any;
  title: string;
  description: string;
  onOpenInfo: (t: string, d: string) => void;
}) => {
  const [field, , helpers] = useField(fieldConfig.name);
  const isActive = Boolean(field.value);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: isActive ? 'primary.main' : 'divider',
        background: isActive
          ? (theme: any) =>
              `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.main}15 100%)`
          : 'background.paper',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: isActive ? '0px 4px 12px rgba(107, 76, 255, 0.15)' : 'none',
        '&:hover': {
          borderColor: 'primary.light',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={isActive}
            onChange={(e) => helpers.setValue(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography sx={{ fontWeight: isActive ? 600 : 400, transition: '0.2s' }}>
            {fieldConfig.label}
          </Typography>
        }
        sx={{ m: 0, flexGrow: 1 }}
      />
      <IconButton size="small" color="info" onClick={() => onOpenInfo(title, description)}>
        <InfoOutlined fontSize="small" />
      </IconButton>
    </Box>
  );
};

export const PlanesForm = ({ formField, isEditing = false }: any) => {
  const [infoModalConfig, setInfoModalConfig] = useState({
    open: false,
    title: '',
    description: '',
  });

  const handleOpenInfo = (title: string, description: string) => {
    setInfoModalConfig({ open: true, title, description });
  };

  const {
    name,
    description,
    monthlyPrice,
    currency,
    monthlyCredits,
    durationDays,
    maxRequestsDay,
    maxRequestsMonth,
    maxAssistants,
    maxAgents,
    maxUsers,
    allowChat,
    allowImages,
    allowAudio,
    allowDocuments,
    allowEmbeddings,
    allowMemory,
    allowTools,
    allowRag,
    sortOrder,
  } = planesFormModel.formField;

  return (
    <Grid container spacing={3}>
      {/* Sección de Información Básica */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Información Básica
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 12 }}>
        <InputField name={name.name} label={name.label} fullWidth />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <InputField
          name={description.name}
          label={description.label}
          multiline
          rows={3}
          fullWidth
        />
      </Grid>

      {/* Sección Comercial */}
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Detalles Comerciales
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <InputUSD name={monthlyPrice.name} label={monthlyPrice.label} fullWidth />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <InputField
          name={monthlyCredits.name}
          label={monthlyCredits.label}
          type="number"
          fullWidth
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <InputField name={durationDays.name} label={durationDays.label} type="number" fullWidth />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <InputField name={sortOrder.name} label={sortOrder.label} type="number" fullWidth />
      </Grid>

      {/* Sección de Límites */}
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Límites del Plan
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <InputField
          name={maxRequestsDay.name}
          label={maxRequestsDay.label}
          type="number"
          fullWidth
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <InputField
          name={maxRequestsMonth.name}
          label={maxRequestsMonth.label}
          type="number"
          fullWidth
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <InputField name={maxAssistants.name} label={maxAssistants.label} type="number" fullWidth />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 6 }}>
        <InputField name={maxAgents.name} label={maxAgents.label} type="number" fullWidth />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 6 }}>
        <InputField name={maxUsers.name} label={maxUsers.label} type="number" fullWidth />
      </Grid>

      {/* Sección de Capacidades (Switches) */}
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Capacidades Incluidas
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <CustomSwitch
          fieldConfig={allowChat}
          title="Permitir Chat"
          description="Habilita la capacidad de tener conversaciones interactivas estilo chat con la IA."
          onOpenInfo={handleOpenInfo}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <CustomSwitch
          fieldConfig={allowImages}
          title="Permitir Imágenes"
          description="Permite generar y procesar imágenes mediante IA."
          onOpenInfo={handleOpenInfo}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <CustomSwitch
          fieldConfig={allowAudio}
          title="Permitir Audio"
          description="Habilita servicios de transcripción y generación de voz."
          onOpenInfo={handleOpenInfo}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <CustomSwitch
          fieldConfig={allowDocuments}
          title="Permitir Documentos"
          description="Capacidad para analizar y extraer datos de documentos (PDF, DOCX)."
          onOpenInfo={handleOpenInfo}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <CustomSwitch
          fieldConfig={allowEmbeddings}
          title="Permitir Embeddings"
          description="Habilita la vectorización de texto para búsquedas semánticas avanzadas."
          onOpenInfo={handleOpenInfo}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <CustomSwitch
          fieldConfig={allowMemory}
          title="Permitir Memoria"
          description="Permite que el asistente recuerde el contexto de conversaciones pasadas."
          onOpenInfo={handleOpenInfo}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <CustomSwitch
          fieldConfig={allowTools}
          title="Permitir Herramientas"
          description="Permite al modelo de IA ejecutar funciones externas (búsqueda web, APIs)."
          onOpenInfo={handleOpenInfo}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <CustomSwitch
          fieldConfig={allowRag}
          title="Permitir RAG"
          description="Retrieval-Augmented Generation. Permite respuestas basadas en una base de conocimientos específica."
          onOpenInfo={handleOpenInfo}
        />
      </Grid>

      <InfoModal
        open={infoModalConfig.open}
        setOpen={(open: boolean) => setInfoModalConfig((prev) => ({ ...prev, open }))}
        title={infoModalConfig.title}
        description={infoModalConfig.description}
        icon="Info"
      />
    </Grid>
  );
};
